// MetricsView.jsx — in-app MySQL metrics tab. Replaces what Prometheus/Grafana
// would do: polls /api/admin/metrics/mysql every 10s and renders pool gauges,
// throughput, latency percentiles, cache effectiveness, errors, and the slow-
// query log. Recharts is already used elsewhere (QuestDBPanel.jsx) so no new
// client dep.

import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config.js';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { RefreshCw, Activity, Database, Zap, AlertTriangle } from 'lucide-react';
import eventStream from '../services/eventStream.js';

// Polling is now a fallback — the SSE channel pushes pool.stats / pool.breaker
// events for instant updates. We still fetch on a long interval (60s) so
// historical bars stay current and the user has a recovery path if SSE drops.
const POLL_MS = 60_000;
const RANGES = [
  { id: '1h',  label: 'Last hour'  },
  { id: '6h',  label: 'Last 6 hours' },
  { id: '24h', label: 'Last 24 hours' },
  { id: '7d',  label: 'Last 7 days' }
];

export default function MetricsView() {
  const [range, setRange] = useState('1h');
  const [data, setData]   = useState({ live: null, buckets: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function fetchData() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/metrics/mysql?range=${range}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || `HTTP ${res.status}`);
      setData({ live: json.live || null, buckets: json.buckets || [] });
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // Ask the server's SSE channel to include pool.* events (opt-in because
    // they're chatty — not in the default forwarded set)
    eventStream.setIncludes(['pool']);
    const offs = [
      eventStream.subscribe('pool.stats',   () => fetchData()),
      eventStream.subscribe('pool.breaker', () => fetchData())
    ];
    const t = setInterval(fetchData, POLL_MS);
    return () => {
      offs.forEach(off => off());
      clearInterval(t);
      // Revert SSE includes when leaving this view
      eventStream.setIncludes([]);
    };
  }, [range]);

  // Prepare chart series — collapse bucket docs into Recharts-friendly rows,
  // then append a synthetic "now" point from the live in-process snapshot so
  // the rightmost data is the current (not-yet-closed) minute. Without this
  // the user sees blank charts for the first 60s after server start.
  const series = useMemo(() => {
    const rows = (data.buckets || []).map(b => {
      const total = (b.cacheHits || 0) + (b.cacheCoalesced || 0) + (b.cacheMisses || 0);
      return {
        ts: new Date(b.bucketStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        queries: b.queries || 0,
        errors:  b.errors  || 0,
        p50: b.p50 || 0, p95: b.p95 || 0, p99: b.p99 || 0,
        cacheHitRate: total > 0 ? Math.round(((b.cacheHits + b.cacheCoalesced) / total) * 100) : 0,
        poolFree:   b.poolFree   || 0,
        poolTotal:  b.poolTotal  || 0,
        poolQueued: b.poolQueued || 0,
        exportQueued:  b.exportJobsQueued  || 0,
        exportRunning: b.exportJobsRunning || 0
      };
    });
    if (data.live) {
      const l = data.live;
      const liveTotal = (l.cacheHits || 0) + (l.cacheCoalesced || 0) + (l.cacheMisses || 0);
      const liveCachePct = liveTotal > 0 ? Math.round(((l.cacheHits + l.cacheCoalesced) / liveTotal) * 100) : 0;
      const poolTotal  = (l.pools || []).reduce((s, p) => s + (p.total  || 0), 0);
      const poolFree   = (l.pools || []).reduce((s, p) => s + (p.free   || 0), 0);
      const poolQueued = (l.pools || []).reduce((s, p) => s + (p.queued || 0), 0);
      rows.push({
        ts: 'now',
        queries: l.queries || 0,
        errors:  l.errors  || 0,
        p50: 0, p95: 0, p99: l.maxMs || 0,        // percentiles unavailable until bucket closes
        cacheHitRate: liveCachePct,
        poolFree, poolTotal, poolQueued,
        exportQueued: 0, exportRunning: 0
      });
    }
    return rows;
  }, [data.buckets, data.live]);

  // Aggregate slow-query log across the window — keep top 20 by max duration
  const slowQueries = useMemo(() => {
    const all = [];
    for (const b of (data.buckets || [])) for (const q of (b.slowQueries || [])) all.push(q);
    return all.sort((a, b) => b.durationMs - a.durationMs).slice(0, 20);
  }, [data.buckets]);

  const errorBreakdown = useMemo(() => {
    const acc = {};
    for (const b of (data.buckets || [])) {
      for (const [code, n] of Object.entries(b.errorsByCode || {})) {
        acc[code] = (acc[code] || 0) + n;
      }
    }
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [data.buckets]);

  const live = data.live;
  const totalQueries = series.reduce((s, b) => s + b.queries, 0);
  const totalErrors  = series.reduce((s, b) => s + b.errors,  0);
  const errRate = totalQueries > 0 ? ((totalErrors / totalQueries) * 100).toFixed(2) : '0.00';

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}><Activity size={20} /> MySQL Metrics</h2>
          <div style={s.subtitle}>
            In-process collector — 60s rollups persisted to MongoDB.
            {loading ? ' Loading…' : ` Updated ${new Date().toLocaleTimeString()}`}
          </div>
        </div>
        <div style={s.controls}>
          {RANGES.map(r => (
            <button key={r.id}
              style={{ ...s.rangeBtn, ...(range === r.id ? s.rangeBtnActive : {}) }}
              onClick={() => setRange(r.id)}>{r.label}</button>
          ))}
          <button style={s.refreshBtn} onClick={fetchData} title="Refresh now">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}
      {!error && series.length <= 1 && (data.live?.queries || 0) === 0 && (
        <div style={{ ...s.empty, border: `1px solid ${C.cardBorder}`, borderRadius: 8, background: C.cardBg }}>
          No MySQL activity recorded yet. Issue a query through the Reports or MySQL panel — the live counter updates instantly,
          and the first historical bucket appears after the next minute boundary.
        </div>
      )}

      {/* Top KPI strip */}
      <div style={s.kpis}>
        <Kpi icon={<Zap size={18} />}     label="Queries (window)"    value={totalQueries.toLocaleString()} />
        <Kpi icon={<AlertTriangle size={18} />} label="Error rate"     value={`${errRate}%`} bad={errRate > 1} />
        <Kpi icon={<Database size={18} />} label="Pool free (live)"
             value={live?.pools?.length ? `${live.pools.reduce((s,p)=>s+(p.free||0),0)} / ${live.pools.reduce((s,p)=>s+(p.total||0),0)}` : '—'} />
        <Kpi icon={<Activity size={18} />} label="Live samples / min"
             value={live?.sampleCount ?? 0} />
      </div>

      <div style={s.grid}>
        <Card title="Query throughput">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="ts" tick={tickStyle} stroke={chartGrid} />
              <YAxis allowDecimals={false} tick={tickStyle} stroke={chartGrid} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="queries" stroke={C.accent} fill="rgba(167, 139, 250, 0.25)" name="Queries" />
              <Area type="monotone" dataKey="errors"  stroke={C.bad}    fill="rgba(239, 68, 68, 0.2)"   name="Errors" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Latency (ms, percentiles)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="ts" tick={tickStyle} stroke={chartGrid} />
              <YAxis tick={tickStyle} stroke={chartGrid} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ color: C.textMuted }} />
              <Line type="monotone" dataKey="p50" stroke={C.good} dot={false} />
              <Line type="monotone" dataKey="p95" stroke={C.warn} dot={false} />
              <Line type="monotone" dataKey="p99" stroke={C.bad}  dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Cache hit rate (%)">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="ts" tick={tickStyle} stroke={chartGrid} />
              <YAxis domain={[0, 100]} tick={tickStyle} stroke={chartGrid} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="cacheHitRate" stroke={C.accent} fill="rgba(167, 139, 250, 0.25)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Connection pool">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="ts" tick={tickStyle} stroke={chartGrid} />
              <YAxis allowDecimals={false} tick={tickStyle} stroke={chartGrid} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ color: C.textMuted }} />
              <Bar dataKey="poolTotal"  stackId="a" fill="#475569" name="Total" />
              <Bar dataKey="poolFree"   stackId="b" fill={C.good}  name="Free" />
              <Bar dataKey="poolQueued" stackId="c" fill={C.bad}   name="Queued" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Export jobs">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey="ts" tick={tickStyle} stroke={chartGrid} />
              <YAxis allowDecimals={false} tick={tickStyle} stroke={chartGrid} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ color: C.textMuted }} />
              <Area type="monotone" dataKey="exportRunning" stroke={C.info} fill="rgba(34, 211, 238, 0.2)" name="Running" />
              <Area type="monotone" dataKey="exportQueued"  stroke={C.warn} fill="rgba(245, 158, 11, 0.2)" name="Queued" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Errors by code (window total)">
          {errorBreakdown.length === 0
            ? <div style={s.empty}>No errors in this window.</div>
            : <table style={s.table}>
                <thead><tr><th style={s.th}>Code</th><th style={s.th}>Count</th></tr></thead>
                <tbody>
                  {errorBreakdown.map(([code, n]) => (
                    <tr key={code}><td style={s.td}>{code}</td><td style={s.tdRight}>{n}</td></tr>
                  ))}
                </tbody>
              </table>}
        </Card>
      </div>

      <Card title="Slow query log (top 20)">
        {slowQueries.length === 0
          ? <div style={s.empty}>No queries slower than threshold in this window.</div>
          : <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>When</th>
                  <th style={s.th}>DB</th>
                  <th style={s.th}>Duration</th>
                  <th style={s.th}>Rows</th>
                  <th style={s.th}>SQL</th>
                </tr>
              </thead>
              <tbody>
                {slowQueries.map((q, i) => (
                  <tr key={i}>
                    <td style={s.td}>{new Date(q.at).toLocaleString()}</td>
                    <td style={s.td}>{q.database || '—'}</td>
                    <td style={s.tdRight}>{q.durationMs} ms</td>
                    <td style={s.tdRight}>{q.rowCount ?? '—'}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12, maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {q.sql}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value, bad }) {
  return (
    <div style={{ ...s.kpi, ...(bad ? s.kpiBad : {}) }}>
      <div style={s.kpiHead}>{icon}<span>{label}</span></div>
      <div style={s.kpiValue}>{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>{title}</div>
      {children}
    </div>
  );
}

// Dark Miralys palette — matches Admindashboard / Dashboard.
const C = {
  pageBg:    '#0b0f19',
  cardBg:    '#0f172a',
  cardBorder:'#1e293b',
  inputBg:   '#020617',
  text:      '#e5e7eb',
  textMuted: '#9ca3af',
  accent:    '#a78bfa',
  accentSoft:'rgba(167, 139, 250, 0.15)',
  good:      '#22c55e',
  warn:      '#f59e0b',
  bad:       '#ef4444',
  info:      '#22d3ee',
  grid:      '#1e293b'
};

const s = {
  wrap:      { padding: 20, display: 'flex', flexDirection: 'column', gap: 16, background: C.pageBg, color: C.text, minHeight: '100%' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
  title:     { display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 22, color: C.text },
  subtitle:  { color: C.textMuted, fontSize: 13, marginTop: 4 },
  controls:  { display: 'flex', gap: 6, alignItems: 'center' },
  rangeBtn:  { padding: '6px 12px', border: `1px solid ${C.cardBorder}`, background: C.inputBg, color: C.textMuted, borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  rangeBtnActive: { background: C.accentSoft, color: C.accent, borderColor: C.accent },
  refreshBtn:{ padding: 6, border: `1px solid ${C.cardBorder}`, background: C.inputBg, color: C.textMuted, borderRadius: 6, cursor: 'pointer' },
  error:     { padding: 10, background: 'rgba(239, 68, 68, 0.1)', color: '#fca5a5', borderRadius: 6, border: '1px solid rgba(239, 68, 68, 0.3)' },
  kpis:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  kpi:       { padding: 14, border: `1px solid ${C.cardBorder}`, borderRadius: 8, background: C.cardBg },
  kpiBad:    { borderColor: 'rgba(239, 68, 68, 0.5)', background: 'rgba(239, 68, 68, 0.08)' },
  kpiHead:   { display: 'flex', gap: 6, alignItems: 'center', color: C.textMuted, fontSize: 13 },
  kpiValue:  { fontSize: 22, fontWeight: 600, marginTop: 4, color: C.text },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12 },
  card:      { padding: 14, border: `1px solid ${C.cardBorder}`, borderRadius: 8, background: C.cardBg },
  cardTitle: { fontWeight: 600, marginBottom: 8, fontSize: 14, color: C.text },
  empty:     { padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: 13, color: C.text },
  th:        { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${C.cardBorder}`, color: C.textMuted, fontWeight: 600 },
  td:        { padding: '6px 8px', borderBottom: `1px solid ${C.cardBorder}` },
  tdRight:   { padding: '6px 8px', borderBottom: `1px solid ${C.cardBorder}`, textAlign: 'right' }
};

// Shared Recharts theme — dark grid, light text. Applied via `tickStyle` props below.
const chartGrid = C.grid;
const tickStyle = { fill: C.textMuted, fontSize: 11 };
const tooltipStyle = {
  contentStyle: { background: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: 6, color: C.text },
  labelStyle:   { color: C.textMuted },
  itemStyle:    { color: C.text }
};
